/**
 * Contract between the app shell (`AppShell.vue` in the consuming app)
 * and section route layouts that render their own secondary sidebar
 * (e.g. administration, mail). Replaces the retired `useAppShell`
 * injection mechanism: rail forcing is declared via route meta, and the
 * mobile Sheet integration goes through a Teleport outlet.
 *
 * Lives in `@gat-ship/vue-components` for the same structural reason
 * `useAppShell` did (DL-19): feature packages cannot import from
 * `apps/gs-cloud`, so the shared vocabulary sits in the lowest common
 * layer both sides already depend on.
 */
import { createContext } from 'reka-ui'
import type { Ref } from 'vue'

/**
 * Teleport target id rendered by the app sidebar inside its mobile
 * Sheet. Section layouts teleport their menu markup here when
 * `useSidebar()` reports `isMobile && openMobile` — the target only
 * exists while the Sheet is open (its content is Presence-gated), so
 * the Teleport must be `v-if`-gated on `openMobile` and use `defer`.
 */
export const APP_SHELL_SECTION_OUTLET_ID = 'app-shell-section-outlet'

/**
 * Registration a section layout makes while its sidebar menu is active,
 * so the app sidebar can offer a panel toggle ("Navigation" vs the
 * section menu) inside the mobile Sheet. Menu *markup* still travels
 * through the Teleport outlet — this carries only the metadata the
 * Sheet chrome needs.
 */
export interface AppShellSectionMenu {
  /** Toggle label for the section panel (e.g. 'Admin', 'Mail'). */
  label: string
}

/**
 * Provided by `AppShell.vue` as a writable **shallowRef**. Section
 * layouts set it to their own stable object while their menu is
 * teleported and clear it on unmount — guarded by object identity
 * (`toRaw(ref.value) === mine`) because on section→section navigation
 * the new layout's setup runs before the old layout's unmount. Both
 * sides of that guard matter: a deep ref would wrap the object in a
 * reactive proxy and silently defeat the `===`, leaving a stale
 * registration behind (hence shallowRef on the provider and `toRaw`
 * at the comparison).
 */
export const [useAppShellSectionMenu, provideAppShellSectionMenu] =
  createContext<Ref<AppShellSectionMenu | null>>('app-shell-section-menu')

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Set `true` on a section route record whose layout renders its own
     * secondary sidebar (or otherwise wants the main sidebar railed,
     * e.g. `mail-compose`). `AppShell.vue` watches the deepest matched
     * record carrying this flag: when that record changes, the main
     * sidebar is forced to rail (entering) or expanded (leaving) once;
     * in between, the user's manual toggle stays free.
     */
    railMainSidebar?: boolean
  }
}
